<template>
  <PageContainer>
    <Title>
      🏢
      {{ company?.name }}
    </Title>
    <PageHeader
      :breadcrumb-item="company?.name"
      :breadcrumb-loading="isLoading"
      has-back-button
      back-route="/companies"
    />
    <PageBody>
      <DetailsContainer :gradient-color="currentSection?.section.color" class="details-container-full-height">
        <div
          v-if="company?.risk_level === 'high'"
          class="flex flex-row gap-1 bg-critical-100 color-critical-600 p-3 rounded-lg items-center mb-3"
        >
          <AwIcon name="i-ph-warning-octagon-fill" class="size-4" color="critical" />
          {{ $t("companies.detail.risk.label") }}
          <div v-if="company?.risk_updated_at">
            {{
              $t("companies.detail.risk.reportedOn", {
                date: dateFormat(company?.risk_updated_at, { options: { dateStyle: "medium" } }),
              })
            }}
          </div>
          <div v-if="company?.risk_updated_by">
            {{ $t("companies.detail.risk.reportedBy", { name: getFullName(company?.risk_updated_by) }) }}
          </div>
          <div v-if="company?.risk_reason">
            {{ $t("companies.detail.risk.reason", { reason: company?.risk_reason }) }}
          </div>
          <div v-if="isAccounting" class="ml-auto">
            <CompanyRemoveRiskModal
              :company="company"
              @on-update="
                (c) => {
                  company = c;
                }
              "
            />
          </div>
        </div>
        <div class="mb-2 flex flex-row gap-2 items-center">
          <AwSkeleton v-if="!company || isLoading" class="w-40 h-6" />
          <AwBadge
            v-else-if="company.activity_status"
            :color="company.activity_status === 'active' ? 'success' : 'critical'"
            :label="translateActivityStatus.fr[company.activity_status]"
            variant="light"
            class="activity-status-badge"
          />
          <div v-else class="h-6"></div>
          <div class="ml-auto flex flex-row gap-2 items-center">
            <CompanyPappersSyncButton
              v-if="company"
              :company="company"
              @on-update="
                (c) => {
                  company = c;
                }
              "
            />
            <CompanyUpdateRiskModal
              v-if="isAccounting && company?.risk_level === 'none'"
              :company="company"
              @on-update="
                (c) => {
                  company = c;
                }
              "
            />
          </div>
        </div>
        <div v-if="currentSection && company && !isLoading" class="flex flex-row gap-1">
          <div class="header-section" :style="`color: ${currentSection.section.color}`">
            <AwBadge
              class="p-1 flex items-center justify-center"
              size="md"
              variant="solid"
              :color="currentSection.section.color"
            >
              <AwIcon :name="`${currentSection.item.icon}-fill`" class="size-3" color="white" />
            </AwBadge>
          </div>
          <div class="header-title">
            {{ company?.name }}
          </div>
        </div>
        <AwSkeleton v-else class="w-80 h-10" />
        <div class="mt-1">
          <DetailsHeaderBadges v-if="company && !isLoading" :badges="getCompanyBadges(company)" />
          <div v-else class="flex flex-row gap-2">
            <AwSkeleton class="w-30 h-6" />
            <AwSkeleton class="w-40 h-6" />
          </div>
        </div>
        <div class="mt-2">
          <DetailsHeaderInformation v-if="company && !isLoading" :information="getCompanyInformation(company)" />
          <div v-else class="flex flex-row gap-2">
            <AwSkeleton class="w-50 h-6" />
            <AwSkeleton class="w-70 h-6" />
            <AwSkeleton class="w-30 h-6" />
          </div>
        </div>
        <div v-if="company && !isLoading" class="mt-4 mb-4">
          <AwTabs
            :model-value="tab"
            :items="tabs"
            default-value="details"
            style="width: 100%; gap: 0px"
            @update:model-value="tab = $event"
          />
        </div>
        <div v-else class="flex flex-row gap-2 mt-3 mb-3">
          <AwSkeleton class="w-15 h-6" />
          <AwSkeleton class="w-15 h-6" />
          <AwSkeleton class="w-15 h-6" />
        </div>
        <div v-if="tab === 'details'" class="tab-content">
          <CompanyDetails v-if="company && !isLoading" :company="company" />
          <div v-else class="flex flex-row gap-2 h-full">
            <div class="flex flex-col gap-2">
              <AwSkeleton class="w-50 h-10" />
              <AwSkeleton class="w-50 h-10" />
            </div>
            <AwSkeleton class="w-full h-full" />
          </div>
        </div>
        <div v-if="tab === 'contacts'" class="tab-content">
          <CompanyContacts v-if="company && !isLoading" :company-id="company.id" />
          <AwSkeleton v-else class="w-full h-full" />
        </div>
        <div v-if="tab === 'credit-insurance'" class="tab-content">
          <CompanyCreditGuarantee v-if="company && !isLoading" :company-id="company.id" />
          <AwSkeleton v-else class="w-full h-full" />
        </div>
        <div v-if="tab === 'contracts'" class="tab-content">
          <CompanyContracts v-if="company && !isLoading" :company-id="company.id" />
          <AwSkeleton v-else class="w-full h-full" />
        </div>
        <div v-if="tab === 'agencies'" class="tab-content">
          <CompanyAgencies v-if="company && !isLoading" :company-id="company.id" />
          <AwSkeleton v-else class="w-full h-full" />
        </div>
        <div v-if="tab === 'commercial-conditions'" class="tab-content">
          <CompanyCommercialConditions v-if="company && !isLoading" :company-id="company.id" />
          <AwSkeleton v-else class="w-full h-full" />
        </div>
        <div v-if="tab === 'missions'" class="tab-content">
          <CompanyMissions v-if="company && !isLoading" :company-id="company.id" />
          <AwSkeleton v-else class="w-full h-full" />
        </div>
        <div v-if="tab === 'wanted-profiles'" class="tab-content">
          <WantedProfileList v-if="company && !isLoading" :company-id="company.id" />
          <AwSkeleton v-else class="w-full h-full" />
        </div>
        <div v-if="tab === 'tasks'" class="tab-content">
          <TaskSubjectList v-if="company && !isLoading" entity="companies" :entity-id="company.id" />
          <AwSkeleton v-else class="w-full h-full" />
        </div>
      </DetailsContainer>
    </PageBody>
  </PageContainer>
</template>

<script setup lang="ts">
import { useUserMe } from "~/components/user/composables/useUser";
import { useCreditInsuranceAccess } from "~/components/company/composables/useCreditInsuranceAccess";
import { useApi } from "~/composables/useApi";
import { useFeatureFlag } from "~/composables/useFeatureFlag";
import { useNavBarData } from "~/composables/useNavBarData";
import type { Color } from "@asap/design/colors";
import { dateFormat } from "@asap/shared/utils/date";
import { getFullName } from "@asap/shared/utils/people";
import type { Company, TimesheetMethodLabel } from "@asap/shared/schemas/company";
import {
  translateActivityStatus,
  translateCompanyCategory,
  translateTimesheetMethodLabel,
} from "@asap/shared/utils/translate";

const { t } = useI18n();
const { isFeatureFlagEnabled, isInitialized: areFeatureFlagsInitialized } = useFeatureFlag();
const isTodoListEnabled = isFeatureFlagEnabled("todo-list");
const isWantedProfilesEnabled = isFeatureFlagEnabled("wanted-profiles");
const { getCurrentSection } = useNavBarData();

const api = useApi();
const route = useRoute();
const router = useRouter();
const { isAccounting } = useUserMe();
const { isCreditInsuranceEnabled, ensureAccessLoaded } = useCreditInsuranceAccess();
const currentSection = getCurrentSection();

const isLoading = ref(true);
const tabs = computed(() => [
  { label: t("companies.detail.tabs.details"), value: "details" },
  { label: t("companies.detail.tabs.contacts"), value: "contacts" },
  ...(isCreditInsuranceEnabled.value
    ? [{ label: t("companies.detail.tabs.creditInsurance"), value: "credit-insurance" }]
    : []),
  { label: t("companies.detail.tabs.commercialConditions"), value: "commercial-conditions" },
  { label: t("companies.detail.tabs.contracts"), value: "contracts" },
  { label: t("companies.detail.tabs.agencies"), value: "agencies" },
  { label: t("companies.detail.tabs.missions"), value: "missions" },
  ...(isWantedProfilesEnabled.value
    ? [{ label: t("companies.detail.tabs.wantedProfiles"), value: "wanted-profiles" }]
    : []),
  ...(isTodoListEnabled.value ? [{ label: t("tasks.nav.title"), value: "tasks" }] : []),
]);

const getInitialTab = () => {
  const tabFromQuery = route.query.tab;
  const validTabs = tabs.value.map((tabItem) => tabItem.value);
  return typeof tabFromQuery === "string" && validTabs.includes(tabFromQuery) ? tabFromQuery : "details";
};

const tab = ref<string | number>(getInitialTab());
const company = ref<Company | undefined>(undefined);

// Feature flags load after the first render in production: pick the tab of the URL again once they are known
watch(areFeatureFlagsInitialized, () => {
  tab.value = getInitialTab();
});

watch(tab, (newTab) => {
  const query = { ...route.query };
  query.tab = String(newTab);
  router.replace({ query });
});

watch(
  () => route.query.tab,
  (newTab) => {
    if (newTab && typeof newTab === "string") {
      const validTabs = tabs.value.map((tabItem) => tabItem.value);
      if (validTabs.includes(newTab)) {
        tab.value = newTab;
      }
    } else {
      tab.value = "details";
      router.replace({ query: { ...route.query, tab: "details" } });
    }
  }
);

const getCompanyBadges = (company: Company) => {
  const badges: { icon?: string; label: string; color: Color }[] = [];

  if (company.collective_proceedings_in_progress === true) {
    badges.push({
      icon: "i-ph-warning-fill",
      label: t("companies.detail.badges.collectiveProceedings"),
      color: "orange",
    });
  }
  if (company.last_synced_at) {
    badges.push({
      icon: "i-ph-arrows-clockwise-duotone",
      label: t("companies.detail.badges.lastPappersSync", {
        date: dateFormat(company.last_synced_at, { options: { dateStyle: "medium" } }),
      }),
      color: "gray",
    });
  } else {
    badges.push({
      icon: "i-ph-arrows-clockwise-duotone",
      label: t("companies.detail.badges.neverSyncedWithPappers"),
      color: "gray",
    });
  }
  badges.push({
    icon: "i-ph-house-line-duotone",
    label: t("companies.detail.badges.agencies", { count: company.total_agencies }, company.total_agencies),
    color: "gray",
  });
  return badges;
};

const getTimesheetMethodsInformation = (company: Company) => {
  const totalTimesheetMethods = company.timesheet_methods.reduce((acc, method) => acc + method.count, 0);

  if (totalTimesheetMethods === 0) {
    return null;
  }

  return {
    details: company.timesheet_methods.map((method) => ({
      label: method.label,
      count: method.count,
      frontLabel: `${translateTimesheetMethodLabel(method.label)} (${(method.count / totalTimesheetMethods) * 100 > 1 ? ((method.count / totalTimesheetMethods) * 100).toFixed(0) : "<1"}%)`,
    })),
    totalTimesheetMethods,
  };
};

const getEmployeeInformation = (company: Company) => {
  if (
    company.min_employee_number !== null &&
    company.max_employee_number !== null &&
    company.employee_updated_year !== null
  ) {
    return t("companies.detail.employees.rangeWithYear", {
      min: company.min_employee_number,
      max: company.max_employee_number,
      year: company.employee_updated_year,
    });
  } else if (company.min_employee_number !== null && company.max_employee_number !== null) {
    return t("companies.detail.employees.range", {
      min: company.min_employee_number,
      max: company.max_employee_number,
    });
  } else if (company.employee_updated_year !== null) {
    return t("companies.detail.employees.updatedIn", { year: company.employee_updated_year });
  } else {
    return null;
  }
};

const getCompanyInformation = (company: Company) => {
  const information: { label?: string; content: { value: string; isCopiable: boolean; tooltip?: string }[] }[] = [];

  if (company.siren) {
    information.push({
      label: t("companies.detail.information.siren"),
      content: [{ value: company.siren, isCopiable: true }],
    });
  }
  const naf = company.naf_code || company.naf_code_label;
  const nafTooltip = company.naf_code ? company.naf_code_label : undefined;
  if (naf) {
    information.push({
      label: t("companies.detail.information.naf"),
      content: [{ value: naf, isCopiable: true, tooltip: nafTooltip || undefined }],
    });
  }
  if (company.business_area) {
    information.push({ content: [{ value: company.business_area, isCopiable: true }] });
  }
  if (company.vat_number) {
    information.push({
      label: t("companies.detail.information.vat"),
      content: [{ value: company.vat_number, isCopiable: true }],
    });
  }
  if (company.company_category) {
    information.push({
      label: t("companies.detail.information.category"),
      content: [{ value: translateCompanyCategory.fr[company.company_category], isCopiable: false }],
    });
  }
  const employeeInformation = getEmployeeInformation(company);
  if (employeeInformation) {
    information.push({ content: [{ value: employeeInformation, isCopiable: false }] });
  }

  const methodsInformation = getTimesheetMethodsInformation(company);
  if (methodsInformation) {
    const { details, totalTimesheetMethods } = methodsInformation;

    information.push({
      label: t("companies.detail.information.timesheetTransmission"),
      content: details.map((detail: { label: TimesheetMethodLabel; count: number; frontLabel: string }) => ({
        value: detail.frontLabel,
        isCopiable: false,
        tooltip: t("companies.detail.information.timesheetMethodCount", {
          count: detail.count,
          total: totalTimesheetMethods,
        }),
      })),
    });
  }
  if (company.formatted_address) {
    information.push({
      label: t("companies.detail.information.headquartersAddress"),
      content: [{ value: company.formatted_address, isCopiable: true }],
    });
  }
  return information;
};

onMounted(async () => {
  if (!route.query.tab) {
    router.replace({ query: { ...route.query, tab: "details" } });
  }
  const [response] = await Promise.all([
    api.invoke({
      path: "companies/:id",
      method: "GET",
      params: { id: String(route.params.id) },
    }),
    ensureAccessLoaded(),
  ]);

  if (response.success) company.value = response.data;
  tab.value = getInitialTab();
  isLoading.value = false;
});
</script>

<style scoped lang="scss">
.header-section {
  display: flex;
  align-items: center;
  gap: 6px;
}

.header-title {
  color: var(--gray-1);
  font-size: 25px;
  font-weight: 500;
}

.activity-status-badge {
  font-weight: 600;
}

.tab-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.details-container-full-height {
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow-y: hidden;

  :deep(.details-container-content) {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    min-height: 0;
    overflow-y: hidden;
  }
}
</style>
